import { IMsalContext, useMsal } from '@azure/msal-react';
import { useCallback, useEffect, useMemo, memo } from 'react';
import { Box, Stack, useTheme } from '@mui/material';
import { ApiService } from '../../../Services/ApiService';
import logger from '../../../Services/LoggingService';

// Extracted modules
import { useSMEDesignGuidelineState } from './SMEDesignGuidelineTypes';
import { getDynamicStyles } from './SMEDesignGuidelineStyles';
import {
  PerformanceMetricsDisplay,
  ActionButtonsHeader,
  FilterContainer,
  LoadingDisplay,
  DataTable,
  FormDialog
} from './SMEDesignGuidelineComponents';
import {
  createModalHandlers,
  createSortingHandlers,
  createFilterHandlers,
  createFormHandlers,
  createDataProcessingHandlers
} from './SMEDesignGuidelineEventHandlers';
import { createApiOperations } from './SMEDesignGuidelineApi';
import { createTableColumns } from './SMEDesignGuidelineUtils';

/**
 * SMEDesignGuideline component for managing the SME design guidelines using Material UI
 */
export const SMEDesignGuideline: React.FC = () => {
  const ctx: IMsalContext = useMsal();
  const theme = useTheme();
  const apiService = useMemo(() => new ApiService(ctx), [ctx]);
  
  // Use the extracted state management hook
  const state = useSMEDesignGuidelineState();
  
  // Memoize styles to prevent recreation on every render
  const styles = useMemo(() => getDynamicStyles(theme), [theme]);

  // Create data processing handlers
  const dataProcessingHandlers = useMemo(() => 
    createDataProcessingHandlers(state.logPerformance), 
    [state.logPerformance]
  );

  // Create API operations
  const apiOperations = useMemo(() => 
    createApiOperations(apiService, state.logPerformance), 
    [apiService, state.logPerformance]
  );

  // Create modal handlers with proper closure scope
  const modalHandlers = useMemo(() => 
    createModalHandlers(
      state.setFormStateFromItem,
      state.setModalTitle,
      state.setIsDisabled,
      state.setIsModalOpen
    ), 
    [state.setFormStateFromItem, state.setModalTitle, state.setIsDisabled, state.setIsModalOpen]
  );

  // Create sorting handlers
  const sortingHandlers = useMemo(() => 
    createSortingHandlers(
      state.dataColumns,
      state.order,
      state.orderBy,
      state.setOrder,
      state.setOrderBy
    ), 
    [state.dataColumns, state.order, state.orderBy, state.setOrder, state.setOrderBy]
  );

  // Create filter handlers
  const filterHandlers = useMemo(() => 
    createFilterHandlers(
      state.showFilters,
      state.isInitialApiCallComplete,
      state.currentData,
      state.setShowFilters,
      state.setDesignGuidelineFilter,
      state.setIsFiltering,
      state.setFilteredData
    ), 
    [
      state.showFilters,
      state.isInitialApiCallComplete,
      state.currentData,
      state.setShowFilters,
      state.setDesignGuidelineFilter,
      state.setIsFiltering,
      state.setFilteredData
    ]
  );

  // Create wrapped API function with current filter - simplified dependencies
  const getAllDesignGuidelinesWithFilter = useCallback(async () => {
    const filterFunction = (data: any[]) => 
      dataProcessingHandlers.filterDesignGuidelines(data, state.designGuidelineFilter);
    
    await apiOperations.getAllDesignGuidelines(
      state.isInitialApiCallComplete,
      state.currentData,
      state.setCurrentData,
      state.setIsInitialApiCallComplete,
      state.setFilteredData,
      state.setIsLoading,
      filterFunction
    );
  }, [
    apiOperations,
    dataProcessingHandlers,
    state.isInitialApiCallComplete,
    state.currentData,
    state.setCurrentData,
    state.setIsInitialApiCallComplete,
    state.setFilteredData,
    state.setIsLoading
    // Removed state.designGuidelineFilter to prevent circular dependency
  ]);

  // Create wrapped add new design guideline function
  const addNewDesignGuidelineWrapped = useCallback(async () => {
    const filterFunction = (data: any[]) => 
      dataProcessingHandlers.filterDesignGuidelines(data, state.designGuidelineFilter);
    
    await apiOperations.addNewDesignGuideline(
      state.selectedIndustry,
      state.selectedSubIndustry,
      state.selectedApplication,
      state.selectedSubApplication,
      state.selectedPosition,
      state.selectedProcessStage,
      state.selectedProductLine,
      state.designSuggestion,
      state.setIsLoading,
      modalHandlers.hideModal,
      state.setCurrentData,
      state.setFilteredData,
      filterFunction
    );
  }, [
    apiOperations,
    dataProcessingHandlers,
    state.selectedIndustry,
    state.selectedSubIndustry,
    state.selectedApplication,
    state.selectedSubApplication,
    state.selectedPosition,
    state.selectedProcessStage,
    state.selectedProductLine,
    state.designSuggestion,
    state.setIsLoading,
    modalHandlers.hideModal,
    state.setCurrentData,
    state.setFilteredData,
    state.designGuidelineFilter
  ]);

  // Create form handlers
  const formHandlers = useMemo(() => 
    createFormHandlers(
      state.setSelectedIndustry,
      state.setSelectedSubIndustry,
      state.setSelectedApplication,
      state.setSelectedSubApplication,
      state.setSelectedProductLine,
      state.setSelectedPosition,
      state.setSelectedProcessStage,
      state.setDesignSuggestion,
      addNewDesignGuidelineWrapped
    ), 
    [
      state.setSelectedIndustry,
      state.setSelectedSubIndustry,
      state.setSelectedApplication,
      state.setSelectedSubApplication,
      state.setSelectedProductLine,
      state.setSelectedPosition,
      state.setSelectedProcessStage,
      state.setDesignSuggestion,
      addNewDesignGuidelineWrapped
    ]
  );

  // Create table columns with edit handler
  const tableColumns = useMemo(() => 
    createTableColumns(modalHandlers.showModal), 
    [modalHandlers.showModal]
  );

  // Initialize data on component mount - only run once
  useEffect(() => {
    logger.component('SMEDesignGuideline', 'mounted - initializing data');
    let isMounted = true; // Prevent state updates if component unmounts
    
    const initializeData = async () => {
      if (!isMounted) return;
      
      const startTime = performance.now();
      state.setDataColumns(tableColumns);
      state.logPerformance('Set data columns', startTime);
      
      logger.debug('Starting API calls', null, { 
        component: 'SMEDesignGuideline',
        method: 'initializeData'
      });
      
      // Run these in sequence for better debugging
      const unitConfigStart = performance.now();
      await apiOperations.getAllUnitConfigs(
        state.setIndustries,
        state.setSubIndustries,
        state.setApplications,
        state.setSubApplications,
        state.setProductLines,
        state.setProcessStages,
        state.setAlfaLavalPositions
      );
      
      if (!isMounted) return;
      state.logPerformance('getAllUnitConfigs', unitConfigStart);
      
      const guidelinesStart = performance.now();
      // Call API directly without filter dependency to prevent loop
      await apiOperations.getAllDesignGuidelines(
        state.isInitialApiCallComplete,
        state.currentData,
        state.setCurrentData,
        state.setIsInitialApiCallComplete,
        state.setFilteredData,
        state.setIsLoading,
        (data) => dataProcessingHandlers.filterDesignGuidelines(data, state.designGuidelineFilter)
      );
      
      if (!isMounted) return;
      state.logPerformance('getAllDesignGuidelines', guidelinesStart);
      state.logPerformance('Total initialization', startTime);
    };
    
    initializeData();
    
    return () => {
      isMounted = false;
    };
  }, [
    // Only depend on static values and setters that don't change
    tableColumns,
    state.setDataColumns,
    state.logPerformance,
    apiOperations,
    dataProcessingHandlers,
    state.setIndustries,
    state.setSubIndustries,
    state.setApplications,
    state.setSubApplications,
    state.setProductLines,
    state.setProcessStages,
    state.setAlfaLavalPositions,
    state.setCurrentData,
    state.setIsInitialApiCallComplete,
    state.setFilteredData,
    state.setIsLoading
    // Removed getAllDesignGuidelinesWithFilter and state values that change
  ]);

  // Apply filtering when filter changes - simplified dependencies
  useEffect(() => {
    if (state.isInitialApiCallComplete && state.currentData.length > 0) {
      const startTime = performance.now();
      const filtered = dataProcessingHandlers.filterDesignGuidelines(
        state.currentData, 
        state.designGuidelineFilter
      );
      state.setFilteredData(filtered);
      state.logPerformance('Filter effect total', startTime);
    }
  }, [
    state.designGuidelineFilter,
    state.isInitialApiCallComplete,
    // Removed state.currentData to prevent initialization loop
    dataProcessingHandlers,
    state.setFilteredData,
    state.logPerformance
  ]);

  // Memoize sorted data for better performance
  const sortedData = useMemo(() => {
    const startTime = performance.now();
    // Return early if no data or still loading
    if (state.filteredData.length === 0 || state.isLoading) {
      return [];
    }
    const result = dataProcessingHandlers.sortData(
      state.filteredData, 
      state.order, 
      state.orderBy
    );
    const duration = performance.now() - startTime;
    
    logger.debug('Sorted data memoization', {
      resultCount: result.length,
      duration: `${duration.toFixed(2)}ms`
    }, {
      component: 'SMEDesignGuideline',
      method: 'sortedData memo'
    });
    
    return result;
  }, [
    state.filteredData,
    state.isLoading,
    dataProcessingHandlers,
    state.order,
    state.orderBy
  ]);

  // Log when render happens
  useEffect(() => {
    logger.debug('Component rendered', { 
      dataLength: sortedData.length 
    }, {
      component: 'SMEDesignGuideline',
      method: 'render effect'
    });
    
    // Debug state changes
    return () => {
      logger.component('SMEDesignGuideline', 'will update or unmount');
    };
  }, [sortedData.length]);

  // Add debug output for each render
  logger.debug('Rendering SMEDesignGuideline component', {
    isLoading: state.isLoading,
    currentDataLength: state.currentData.length,
    filteredDataLength: state.filteredData.length,
    sortedDataLength: sortedData.length,
    performanceMetrics: state.performanceMetrics
  }, {
    component: 'SMEDesignGuideline',
    method: 'render'
  });

  return (
    <Box sx={styles.container}>
      {/* Performance Metrics Display */}
      <PerformanceMetricsDisplay performanceMetrics={state.performanceMetrics} />
      
      <Stack spacing={3} sx={styles.contentWrapper}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 4,
          mt: 1
        }}>
        </Box>
        
        {/* Action Buttons Header */}
        <ActionButtonsHeader
          onCreateNew={() => modalHandlers.showModal()}
          isLoading={state.isLoading}
          isFiltering={state.isFiltering}
          showFilters={state.showFilters}
          filteredDataLength={state.filteredData?.length || 0}
          onToggleFilters={filterHandlers.toggleShowFilters}
          onResetFilters={filterHandlers.resetFilters}
        />
        
        {/* Filter Container */}
        <FilterContainer
          showFilters={state.showFilters}
          filter={state.designGuidelineFilter}
          isFiltering={state.isFiltering}
          filteredData={state.filteredData}
          currentData={state.currentData}
          onFilterChange={filterHandlers.handleFilterChange}
        />

        {/* Main Content */}
        {state.isLoading ? (
          <LoadingDisplay />
        ) : (
          <Stack spacing={3} alignItems="center" width="100%">
            <DataTable
              filteredData={state.filteredData}
              sortedData={sortedData}
              dataColumns={state.dataColumns}
              order={state.order}
              orderBy={state.orderBy}
              onEdit={modalHandlers.showModal}
              onRequestSort={sortingHandlers.handleRequestSort}
            />
          </Stack>
        )}
      </Stack>

      {/* Form Dialog */}
      <FormDialog
        isOpen={state.isModalOpen}
        title={state.modalTitle}
        isDisabled={state.isDisabled}
        industries={state.industries}
        subIndustries={state.subIndustries}
        applications={state.applications}
        subApplications={state.subApplications}
        productLines={state.productLines}
        processStages={state.processStages}
        alfaLavalPositions={state.alfaLavalPositions}
        selectedIndustry={state.selectedIndustry}
        selectedSubIndustry={state.selectedSubIndustry}
        selectedApplication={state.selectedApplication}
        selectedSubApplication={state.selectedSubApplication}
        selectedProductLine={state.selectedProductLine}
        selectedProcessStage={state.selectedProcessStage}
        selectedPosition={state.selectedPosition}
        designSuggestion={state.designSuggestion}
        onClose={modalHandlers.hideModal}
        onSubmit={formHandlers.handleSubmit}
        onSelectChange={formHandlers.handleSelectChange}
        onDesignSuggestionChange={formHandlers.handleDesignSuggestionChange}
      />
    </Box>
  );
};

export default memo(SMEDesignGuideline); 